import { Property } from '../services/api';

/**
 * 현장 상세 페이지 URL 생성
 * house_manage_no(주택관리번호)가 있으면 우선 사용, 없으면 id 사용
 * @param property 분양 정보 객체 또는 id/house_manage_no
 * @returns URL 경로 (예: "/property/2025000148")
 */
export const getPropertyUrl = (property: Property | { id: number; house_manage_no?: string | null }): string => {
  // house_manage_no가 있으면 우선 사용
  if (property.house_manage_no) {
    return `/property/${property.house_manage_no}`;
  }
  // 없으면 id 사용
  return `/property/${property.id}`;
};

/**
 * 현장 상세 페이지 전체 URL 생성 (SEO용)
 * @param property 분양 정보 객체
 * @param baseUrl 기본 URL (예: "https://voonang.com")
 * @returns 전체 URL (예: "https://voonang.com/property/2025000148")
 */
export const getPropertyFullUrl = (property: Property | { id: number; house_manage_no?: string | null }, baseUrl: string): string => {
  return `${baseUrl}${getPropertyUrl(property)}`;
};

/**
 * 가격 포맷팅 유틸리티
 * @param price 만원 단위 가격
 * @returns 포맷팅된 가격 문자열 (예: "3억 5,000만원")
 */
export const formatPrice = (price: number | undefined): string => {
  if (!price) return '미정';
  if (price >= 10000) {
    const billion = Math.floor(price / 10000);
    const remainder = price % 10000;
    if (remainder === 0) {
      return `${billion}억원`;
    }
    return `${billion}억 ${remainder.toLocaleString()}만원`;
  }
  return `${price.toLocaleString()}만원`;
};

/**
 * 가격 범위 포맷팅
 * @param property 분양 정보 객체
 * @returns 최소~최대 가격 범위 문자열
 */
export const formatPriceRange = (property: Property): string => {
  if (property.min_price && property.max_price) {
    return `${formatPrice(property.min_price)} ~ ${formatPrice(property.max_price)}`;
  } else if (property.min_price) {
    return `${formatPrice(property.min_price)}부터`;
  }
  return '';
};

/**
 * 면적 범위 포맷팅
 * @param property 분양 정보 객체
 * @returns 최소~최대 면적 범위 문자열
 */
export const formatAreaRange = (property: Property): string => {
  if (property.min_area && property.max_area) {
    return `${property.min_area}㎡ ~ ${property.max_area}㎡`;
  } else if (property.min_area) {
    return `${property.min_area}㎡부터`;
  }
  return '';
};

/**
 * 청약 일정 포맷팅
 * @param property 분양 정보 객체
 * @returns 청약 시작~종료일 문자열
 */
export const formatSubscriptionDate = (property: Property): string => {
  if (property.subscription_start && property.subscription_end) {
    return `${property.subscription_start} ~ ${property.subscription_end}`;
  } else if (property.subscription_start) {
    return `${property.subscription_start}부터`;
  }
  return '';
};

/**
 * 오염된 텍스트 필드 정리 (시공사, 시행사 등)
 * @param text 원본 텍스트
 * @returns 정리된 텍스트 (placeholder인 경우 빈 문자열)
 */
export const cleanFieldText = (text: string | null | undefined): string => {
  if (!text) return '';

  // placeholder 값은 빈 문자열 반환
  const placeholders = ['시행사', '시공사', '분양사', '미정', '-', '없음'];
  if (placeholders.includes(text.trim())) return '';

  // 불필요한 키워드 이후 텍스트 제거
  const keywords = ['주차대수', '주차', '입주일', '입주예정', '입주', '분양가', '문의번호', '문의', '세대수', '규모'];
  let cleaned = text;

  for (const keyword of keywords) {
    if (cleaned.includes(keyword)) {
      cleaned = cleaned.split(keyword)[0];
    }
  }

  // 공백 정리
  cleaned = cleaned.replace(/\s+/g, ' ').trim();

  // 너무 짧아지면 원본 반환
  if (cleaned.length < 2) return text;

  return cleaned;
};

/**
 * 규모 필드 정리 (지하/지상 층수 정보만 유지)
 * @param text 원본 텍스트
 * @returns 정리된 텍스트
 */
export const cleanBuildingScale = (text: string | null | undefined): string => {
  if (!text) return '';

  // "지하X층, 지상Y층, Z개동" 패턴만 추출
  const match = text.match(/(지하\s*\d+층[,\s]*)?지상\s*\d+층[,\s]*(\d+개?\s*동)?/);
  if (match) {
    return match[0].trim();
  }

  // 불필요한 키워드 이후 제거
  let cleaned = text;
  const keywords = ['세대수', '타입', '입주', '분양가', '문의'];
  for (const keyword of keywords) {
    if (cleaned.includes(keyword)) {
      cleaned = cleaned.split(keyword)[0];
    }
  }

  return cleaned.replace(/\s+/g, ' ').trim();
};

/**
 * 분양 상태에 따른 CSS 클래스 반환
 * @param status 분양 상태
 * @returns CSS 클래스명
 */
export const getStatusBadgeClass = (status: string): string => {
  switch (status) {
    case '분양예정':
      return 'status-upcoming';
    case '마감임박':
      return 'status-imminent';
    case '분양중':
      return 'status-active';
    case '분양완료':
      return 'status-completed';
    case '계약완료':
      return 'status-closed';
    default:
      return '';
  }
};

/**
 * 입주 예정일 포맷팅 (년, 월만 표시)
 * @param dateStr 날짜 문자열 (예: "2026-06-01")
 * @returns 포맷팅된 문자열 (예: "2026년 06월")
 */
export const formatMoveInDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '';

  // 이미 "년 월" 형식인 경우 그대로 반환
  if (dateStr.includes('년') && dateStr.includes('월')) {
    return dateStr;
  }

  // YYYY-MM-DD 또는 YYYY-MM 형식 처리
  const match = dateStr.match(/^(\d{4})-(\d{1,2})/);
  if (match) {
    const year = match[1];
    const month = match[2].padStart(2, '0');
    return `${year}년 ${month}월`;
  }

  // 다른 형식인 경우 원본 반환
  return dateStr;
};
